import { MatDialog } from '@angular/material/dialog';
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AcumaticaService } from "../acumatica/acumatica.service";
import { Shipment } from "../acumatica/EntityServices/Shipment.service";
import { AppConfig } from '../app-config.service';
import { IPrintCopies, PrinterSelectDialog } from '../common/printer-selector/printer-selector.component';
import { AcumaticaLoadingOptions, LoadingIndicator } from '../acumatica/loading-indicator/AcumaticaLoadingOptions';
import { AcumaticaEntity } from '../acumatica/EntityServices/acumatica-entity';

@Injectable({
    providedIn: 'root'
})
export class WMSService {
    constructor(
        private acumatica: AcumaticaService,
        private dialog: MatDialog
    ) { }

    /**
     * Queues a Print Job in Acumatica to print a Stock Tag
     * @param printerID The Printer ID to send the Tag to
     * @param InventoryID 
     * @param LotSerialNbr 
     * @param ItemQty 
     * @param ReceiptNbr Optional - Override the Receipt Nbr looked up for Lot/Serial items
     * @param VendorID Optional - Override the Vendor ID looked up for Lot/Serial items
     * @param NumberOfCopies Optional - Quantity of tags to print, default 1
     * @param CutCount Optional - Print "xN" # of counts
     * @returns 
     */
    printStockRollTag(
        printerID: string,
        InventoryID: string,
        LotSerialNbr: string,
        ItemQty: number,
        ReceiptNbr?: string,
        VendorID?: string,
        NumberOfCopies: number = 1,
        CutCount: number | null = null,
        loaderOptions: AcumaticaLoadingOptions | null = {
            message: "Printing Stock Tag",
            indicator: LoadingIndicator.snackbar
        }
    ): Observable<void> {
        return new Observable<void>(subscriber => {
            if (ItemQty == 0) {
                // possible when transferring items into cut & hold to have zero quantity left available
                // do not need extra stock tags printed with zero quantity
                subscriber.next();
                subscriber.complete();
                return;
            }

            let params = {
                'Printer': printerID,
                'InventoryID': InventoryID,
                'LotSerialNbr': LotSerialNbr,
                'LabelQty': ItemQty,
                'ReceiptNbr': ReceiptNbr,
                'VendorID': VendorID,
                'CutCount': CutCount
            }

            this.acumatica.action('InventorySummaryInquiry', 'PrintRollTag', undefined, params, loaderOptions).subscribe(() => {
                subscriber.next();
                subscriber.complete();
            });
        });
    }

    printCutAndHoldTag(
        printerID: string,
        ShipmentNbr: string,
        LineNbr: number,
        LotSerialNbr: string,
        Yardage: number,
        loaderOptions: AcumaticaLoadingOptions | undefined = {
            message: "Printing Cut&Hold Tag",
            indicator: LoadingIndicator.snackbar
        }
    ): Observable<void> {
        return new Observable<void>(subscriber => {
            let entity = new AcumaticaEntity({ ShipmentNbr: ShipmentNbr });
            let params = {
                'SOShipLineNbr': LineNbr,
                'Printer': printerID,
                'LotSerialNbr': LotSerialNbr,
                'Yardage': Yardage,
            };

            this.acumatica.action('Shipment', 'PrintCutAndHoldTag', entity, params, loaderOptions).subscribe(() => {
                subscriber.next();
                subscriber.complete();
            });
        })
    }

    printQualityControlTags(
        printerID: string,
        ShipmentNbr: string,
        OrderType: string,
        OrderNbr: string,
        InventoryID: string,
        LotSerialNbr: string,
        NumberOfCopies: number
    ): Observable<void> {
        return new Observable<void>(subscriber => {
            let params = {
                DirectPrint: true,
                Printer: printerID,
                OrderType: OrderType,
                OrderNbr: OrderNbr,
                InventoryCD: InventoryID,
                LotSerialNbr: LotSerialNbr,
                NumberOfCopies: NumberOfCopies
            }

            // Shipment entity reference needed by Acumatica even though not actually run through a Shipment
            let shipment = (<unknown>{ "ShipmentType": "Normal", "ShipmentNbr": ShipmentNbr }) as Shipment
            this.acumatica.action('Shipment', 'PrintShipmentRollTagParams', shipment, params, {
                message: "Printing Quality Control Tags"
            }).subscribe(() => {
                subscriber.next();
            });
        })
    }

    /** Sends API request to Acumatica to print the Roll Tag (and if configured, Customer-specific roll tags) */
    printShipmentRollTag(
        printerID: string,
        ShipmentNbr: string,
        ShipLineNbr: number,
        LotSerialNbr: string,
        Yardage: number,
        Allowance: number,
        loaderOptions: AcumaticaLoadingOptions = {
            message: "Printing Customer Roll Tags"
        }
    ): Observable<void> {
        return new Observable<void>(subscriber => {
            let params = {
                Printer: printerID,
                LineNbr: ShipLineNbr,
                LotSerialNbr: LotSerialNbr,
                Yardage: Yardage,
                Allowance: Allowance
            }

            // Prints through Shipment graph instance even though report does not actually depend on Shipment #
            let shipment = (<unknown>{ "ShipmentType": "Normal", "ShipmentNbr": ShipmentNbr }) as Shipment
            
            this.acumatica.action('Shipment', 'PrintCustomerRollTags', shipment, params, loaderOptions).subscribe(() => {
                subscriber.next();
            });
        })
    }

    printCustomerWillCallTag(
        printerID: string,
        ShipmentNbr: string,
        NumberOfCopies: number,
        loaderOptions: AcumaticaLoadingOptions = {
            message: "Printing Will Call Tag"
        }): Observable<void> {
        return new Observable<void>(subscriber => {
            let params = {
                Printer: printerID,
                NumberOfCopies: NumberOfCopies,
            }

            let shipment = (<unknown>{ "ShipmentType": "Normal", "ShipmentNbr": ShipmentNbr }) as Shipment
            this.acumatica.action('Shipment', 'PrintCustomerWillCallTag', shipment, params, loaderOptions).subscribe(() => {
                subscriber.next();
            });
        });
    }

    // printShipmentNumberLabel(
    //     printerID: string,
    //     shipment: Shipment,
    // ): Observable<void> {
    //     return new Observable<void>(subscriber => {
    //         let params = {
    //             'DirectPrint': true,
    //             'Printer': printerID,
    //         }

    //         this.acumatica.action('Shipment', 'PrintShipmentNumberLabel', { "ShipmentNbr": shipment.ShipmentNbr }, params, {
    //             message: "Printing Shipment # Label"
    //         }).subscribe(() => {
    //             subscriber.next();
    //         });
    //     })
    // }

    printBinLocationTag(
        printerID: string,
        LocationID: string
    ): Observable<void> {
        return new Observable<void>(subscriber => {
            let params = {
                'DirectPrint': true,
                'Printer': printerID,
                'LocationID': LocationID
            }

            // warehouse doesn't matter -- it's just a pass-through to the report
            this.acumatica.action('Warehouse', 'PrintLocationIDTag', { "WarehouseID": "01" }, params, {
                message: "Printing Location Tag"
            }).subscribe(() => {
                subscriber.next();
            });
        })
    }

    lastPrinterUsed: IPrinter | undefined;

    /**
     * Validates provided printerID or dialog prompts user to select printer if not provided
     * @param printerID Printer ID to validate or undefined to force dialog selection
     * @returns IPrinter or undefined if user Cancelled dialog prompt
     */
    selectPrinter(printerID?: string, title?: string, copies?: IPrintCopies): Observable<{ Printer: IPrinter, NumberOfCopies?: number } | undefined> {
        return new Observable<{ Printer: IPrinter } | undefined>(subscriber => {
            let printer = this.validatePrinterID(printerID);

            if (printer !== undefined) {
                subscriber.next({ Printer: printer });
                subscriber.complete();
                return;
            }

            this.dialog.open(PrinterSelectDialog, {data: {
                PrinterID: printerID,
                Title: title,
                NumberOfCopies: copies
            }}).afterClosed().subscribe(subscriber);
        })
    }

    validatePrinterID(printerID?: string): IPrinter | undefined {
        let printer = AppConfig.settings.WarehousePrinters.find(p => p.id == printerID?.toString().trim());
        if (printer !== undefined) this.lastPrinterUsed = printer;
        return printer;
    }
}

export interface IPrinter {
    id: string
}
